import { Box, Text } from '@chakra-ui/react';
import { type FC, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { CardContent } from '$/components/common/Card/CardContent';
import { envVars } from '$/envVars';
import { useDesignGuideStore } from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { type Harmony } from '$/services/usecases/materials/mapper/harmonies';
import { getColorBrightness } from '$/utils/colorUtils';

export interface HarmonyCardProps {
  harmony: Harmony;
  onClick?: (harmony: Harmony) => void;
}

export const HarmonyCard: FC<HarmonyCardProps> = ({ harmony, onClick }) => {
  const { t } = useTranslation();
  const viewMode = useDesignGuideStore.useViewMode();
  const [primary, secondary, tertiary] = harmony.materials;
  const textColor =
    getColorBrightness(primary.hex) > 180 ? 'neutral.1000' : 'neutral.50';
  const harmonyCode = t(
    `dashboard.designGuide.designCodes.${harmony.scene}.${harmony.name?.toUpperCase()}`,
    { defaultValue: '' },
  );

  return (
    <Box
      sx={{ perspective: '1000px' }}
      w='full'
      {...(onClick && {
        _focusVisible: {
          outline: '2px solid',
          outlineColor: 'form.activeBorder',
          outlineOffset: '2px',
        },
        cursor: 'pointer',
        onClick: () => onClick(harmony),
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(harmony);
          }
        },
        role: 'button',
        tabIndex: 0,
      })}
      aria-label={harmony.name}
      data-testid={`harmony-card-${harmony.id}`}
    >
      <Box
        sx={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.55s ease',
          transform: viewMode === 'scene' ? 'rotateX(180deg)' : 'rotateX(0deg)',
          willChange: 'transform',
        }}
        pos='relative'
        w='full'
        aspectRatio='3/2'
      >
        <Box
          sx={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateX(0deg) translateZ(1px)',
            outline: '1px solid transparent',
          }}
          pos='absolute'
          overflow='hidden'
          bg={primary.hex}
          borderWidth='1px'
          borderStyle='solid'
          borderColor='border'
          borderRadius='12px'
          _hover={onClick && { borderColor: 'borderHover' }}
          _active={onClick && { borderColor: 'borderActive' }}
          inset={0}
        >
          <Box
            pos='absolute'
            top='0'
            right='0'
            w='30%'
            h='100%'
            bg={secondary.hex}
          />
          <Box
            pos='absolute'
            bottom='0'
            left='0'
            w='70%'
            h='26%'
            bg={tertiary.hex}
          />
          <Text
            pos='relative'
            zIndex={2}
            pt='5px'
            pl='8px'
            color={textColor}
            fontSize='sm'
            fontWeight='bold'
            lineHeight='shorter'
            textTransform='uppercase'
          >
            {harmony.name}
          </Text>
          <Text
            pos='relative'
            zIndex={2}
            pt='5px'
            pl='8px'
            color={textColor}
            fontSize='xs'
            lineHeight='shorter'
            textTransform='uppercase'
          >
            {harmonyCode}
          </Text>
        </Box>

        <Box
          sx={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateX(180deg) translateZ(1px)',
            outline: '1px solid transparent',
          }}
          pos='absolute'
          overflow='hidden'
          borderRadius='12px'
          inset={0}
        >
          <CardContent
            bg='neutral.300'
            h='full'
            image={`${envVars.VITE_BASE_STORAGE_URL}/spectrum-projectthumbnails/${harmony.inspirationId}-${harmony.id.toLowerCase()}.jpg`}
            label={harmony.name}
            w='full'
          />
        </Box>
      </Box>
    </Box>
  );
};
